import { Box, Typography } from '@mui/material';
import React, { useContext, useState, useRef } from 'react';
import { doc, updateDoc, collection } from 'firebase/firestore';
import { FirestoreDB } from '../../utils/firebase/firebase';
import AuthContext from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { UIContext } from '../../contexts/UIProvider';
import usePatientsForDoctor from '../../hooks/usePatientsForDoctor';
import useStudentsForOneTeacher from '../../hooks/useStudentsForOneTeacher';
import useTeachersForOneStudent from '../../hooks/useTeachersForOneStudent';
import useFetchAllAttachmentsByUser from '../../hooks/useFetchAllAttachmentsByUser';
import useFetchTeachersDataForLogin from '../../hooks/useFetchTeachersDataForLogin';
import NotificationFilters from './NotificationFilters';
import NotificationItem from './NotificationItem';
import { createNotificationHandlers } from './NotificationHandlers';
import { useNotificationState } from './NotificationState';
import { useNotificationEffects } from './NotificationEffects';

const Notification = ({ notifications = [], fetchNotifications }) => {
  const { user, setOpenRightPanel, typeOfUser, setOpenVideoUpload } =
    useContext(AuthContext);
  const { selectPatient } =
    useContext(UIContext);
  const { data: patients = [] } = usePatientsForDoctor(user?.uid);
  const { data: studentData = [] } = useStudentsForOneTeacher(
    user?.uid,
    typeOfUser
  );
  const { data: teacherData = [] } = useTeachersForOneStudent(
    user?.uid,
    typeOfUser
  );
  // eslint-disable-next-line no-unused-vars
  const { data: teachersDataforTeacherlogin = [], refetch: refetchTeachers } =
    useFetchTeachersDataForLogin(user?.uid, user?.schoolId);

  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [groups, setGroups] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [videoList, setVideoList] = useState([]);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const groupsUnsubscribeRef = useRef(null);

  const { data: allAttachments } = useFetchAllAttachmentsByUser(
    user?.uid,
    typeOfUser
  );

  // Use the custom hook for notification state management
  const {
    notificationsList,
    filteredNotifications,
    activeFilter,
    handleFilterChange,
  } = useNotificationState(notifications, typeOfUser);

  // Use the custom hook for notification effects
  const { fetchGroups } = useNotificationEffects(user, fetchNotifications);

  // Set up navigation ref
  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Set up groups listener
  React.useEffect(() => {
    if (!user?.uid) {
      // Clean up if no user
      if (groupsUnsubscribeRef.current) {
        groupsUnsubscribeRef.current();
        groupsUnsubscribeRef.current = null;
      }
      return;
    }
    
    const unsubscribe = fetchGroups(user, setGroups, setLoading);
    groupsUnsubscribeRef.current = unsubscribe;
    
    return () => {
      if (groupsUnsubscribeRef.current) {
        groupsUnsubscribeRef.current();
        groupsUnsubscribeRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const markNotificationAsRead = async (notificationId) => {
    const notificationIndex = notifications.findIndex(
      (notification) => notification.id === notificationId
    );

    if (notificationIndex !== -1) {
      try {
        const notificationRef = doc(
          collection(FirestoreDB, 'notifications', user?.uid, 'messages'),
          notificationId
        );
        await updateDoc(notificationRef, { readStatus: true });
        await fetchNotifications();
      } catch (error) {
        console.error('Error updating readStatus in Firebase:', error);
      }
    }
  };

  // Create handlers using the factory function
  const handlers = createNotificationHandlers({
    user,
    typeOfUser,
    patients,
    studentData,
    teacherData,
    teachersDataforTeacherlogin,
    allAttachments,
    videoList,
    navigate,
    setOpenRightPanel,
    selectPatient,
    markNotificationAsRead,
    setOpenVideoUpload,
  });

  return (
    <Box className='fullWidth' p={2}>
      <NotificationFilters
        activeFilter={activeFilter}
        handleFilterChange={handleFilterChange}
        notificationsList={notificationsList}
      />

      {filteredNotifications?.length > 0 ? (
        filteredNotifications?.map((notification, index) => (
          <NotificationItem
            key={index}
            notification={notification}
            index={index}
            handlers={handlers}
            setOpenRightPanel={setOpenRightPanel}
            markNotificationAsRead={markNotificationAsRead}
          />
        ))
      ) : (
        <Box
          className='fullHeight flexBox flexColumn flexCenter'
          sx={{ py: 8 }}
        >
          <Typography
            variant='h6'
            sx={{
              fontFamily: 'Lato',
              color: '#64748b',
              textAlign: 'center',
            }}
          >
            {activeFilter === 'all'
              ? 'No new notifications found'
              : activeFilter === 'quicktracks'
              ? 'No QuickTracks notifications found'
              : `No ${activeFilter} notifications found`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Notification;
